import { useMachine } from '@xstate/react';
import { FormattedMessage } from 'react-intl';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { applicationParameters } from '../../../../utilities/application-parameters';
import { useSelectedProvider } from '../../../../components/App/ProviderContainer/useSelectedProvider';
import { LoadingOverlay } from '../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { SuccessNotification } from '../../../../core/screens/SuccessNotification/SuccessNotification';
import { ApplicationSuccessResult } from '../../../../models/ApplicationSuccessResult';
import { messages as authorizationMessages } from '../../../../components/AuthorizationDirector/messages';
import { useConfiguration } from '../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { showSuccessScreenForIncomeCheck } from '../../../../models/configuration';
import { ReduxCredentials } from '../../../../components/screens/SubmitCredentials/ReduxCredentials';
import { useAnalyticsCredentialsUpdating } from '../../../../components/hooks/useAnalyticsCredentialsUpdating/useAnalyticsCredentialsUpdating';
import { MultibankingAddProviderScreen } from '../../../components/multibanking/MultibankingAddProviderScreen';
import { getMachine, IncomeCheckMachineViewProps } from './incomeCheckMachine';

// istanbul ignore next: this component is a switch.
export const IncomeCheckMachineView = ({
  engineVersion,
  externalReference,
  onCancel,
  isMultibanking,
}: IncomeCheckMachineViewProps) => {
  useAnalyticsCredentialsUpdating();
  const history = useHistory();

  const machine = useMemo(
    () => getMachine({ engineVersion, externalReference, isMultibanking }),
    [engineVersion, externalReference, isMultibanking]
  );
  const { selectedProvider } = useSelectedProvider();
  const { data: configurationData } = useConfiguration();

  const [current, send] = useMachine(machine);

  if (current.matches('auth')) {
    return (
      <ReduxCredentials
        onBack={() => {
          onCancel();
          // ReduxCredentials doesn't reliably call the onBack callback,
          // instead sometimes doing history.goBack by itself, so we're doing
          // the same to align until we fix the issue.
          // TODO: use send() to manage the navigation in XState
          history.goBack();
        }}
        onDone={() => send('CREDENTIALS')}
        provider={selectedProvider}
        refreshableItems={applicationParameters.get('refreshableItems')}
      />
    );
  }

  if (current.matches('addMultibankingProvider')) {
    return <MultibankingAddProviderScreen onDoneAddingBanks={() => send('DONE_ADDING_BANKS')} />;
  }

  if (current.matches('success')) {
    const message = (
      <FormattedMessage
        {...authorizationMessages.accountConnectedTo}
        values={{ providerName: selectedProvider.displayName }}
      />
    );

    const skipSuccessScreen = !showSuccessScreenForIncomeCheck(configurationData);

    return (
      <SuccessNotification
        overlayHeading={message}
        forceSkipSuccessScreen={skipSuccessScreen}
        applicationSuccessResult={ApplicationSuccessResult.incomeCheck({
          incomeCheckId: current.context.incomeCheckReport!.id,
        })}
      />
    );
  }

  if (current.matches('error')) {
    throw current.context.error;
  }

  return <LoadingOverlay />;
};
